import React from 'react';
import { Link } from 'react-router-dom';
import Modal, { ModalTitle, ModalContent, ModalActions } from '../basic/Modal';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="4xl" maxHeight="80vh" aria-labelledby="privacy-modal-title">
      <ModalTitle id="privacy-modal-title">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Privacy Policy</h2>
            <p className="text-sm text-gray-500 mt-1">How we protect and use your information</p>
          </div>
        </div>
      </ModalTitle>

      <ModalContent className="px-0 py-0 overflow-hidden">
        <div className="h-full overflow-y-auto bg-gradient-to-r from-green-50 to-green-50">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>Last updated: August 22, 2025</span>
              </div>
              <div className="flex items-center space-x-1 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
                <span>Privacy Protected</span>
              </div>
            </div>
          </div>

          <div className="px-6 py-6">
            <div className="text-gray-800 text-sm leading-relaxed max-w-none space-y-4">
              <div className="text-justify">
                <p className="mb-3">
                  This Privacy Policy ("<strong>Privacy Policy</strong>" or "<strong>Terms</strong>") describes how
                  Sports Data Partners, LLC ("<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>"), a
                  Delaware limited liability company, collects, uses, and discloses information in connection with your
                  use of the STATDOG mobile application and/or any related web-based services (the "
                  <strong>Service</strong>").
                </p>

                <p className="mb-3">
                  This Privacy Policy constitutes a legally binding agreement made between you, whether personally or on
                  behalf of an entity, and us concerning your access to and use of our Service. By accessing and
                  continuing your use of our Service, or by checking any authorizations boxes accompanying any links or
                  messages by which you arrived at these Terms, you agree to be bound by these Terms and all other
                  terms, such as our Terms and Conditions, as updated from time to time and incorporated hereto by
                  reference. By accessing or using the Service, you agree to the collection and use of your information
                  in accordance with this policy. If you do not agree with the terms of this policy, please do not
                  access or use the Service.
                </p>

                <p className="mb-6">
                  We reserve the right to change or modify any of the Terms and/or our Service at any time. If we decide
                  to change our Terms, we will notify you by revising the date at the top of the Privacy Policy and you
                  waive any right to receive specific notice of each such change. Your use of our Service following the
                  posting of changes or modifications to the Terms will constitute your acceptance of the revised Terms.
                  Therefore, you should review the Terms and applicable policies from time to time to understand the
                  terms that apply to your use of the Service. If you do not agree to the amended Terms, you must
                  immediately stop using the Service.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                1. Information We Collect
              </h3>

              <p className="text-justify mb-4">
                We collect several types of information for various purposes to provide and improve our Service to you.
              </p>

              <div className="mb-4">
                <h4 className="text-base font-semibold text-green-700 mb-3 mt-5">
                  1.1. Information You Provide to Us:
                </h4>
                <p className="mb-3">
                  When you use the Service, you may provide us with personally identifiable information, including:
                </p>

                <ul className="ml-6 space-y-2 list-disc">
                  <li>
                    <strong className="text-gray-800">Account Information:</strong>
                    <span className="text-gray-700">
                      {' '}
                      Your name, email address, and a password to create and manage your account.
                    </span>
                  </li>
                  <li>
                    <strong className="text-gray-800">User-Generated Content (UGC):</strong>
                    <span className="text-gray-700">
                      {' '}
                      The core functionality of STATDOG involves the input and storage of sports-related data. This
                      includes but is not limited to:
                    </span>
                    <ul className="ml-6 mt-2 space-y-1 list-[circle]">
                      <li className="text-gray-700">
                        Player names and certain identifying information (jersey number, graduation year, position,
                        etc.).
                      </li>
                      <li className="text-gray-700">Team names and rosters.</li>
                      <li className="text-gray-700">Lacrosse game statistics and performance data.</li>
                    </ul>
                  </li>
                </ul>
              </div>

              <div className="mb-4">
                <h4 className="text-base font-semibold text-green-700 mb-3 mt-5">
                  1.2. Information We Collect Automatically:
                </h4>
                <p className="mb-3">
                  When you access the Service, we may automatically collect certain information from your device,
                  including:
                </p>

                <ul className="ml-6 space-y-2 list-disc">
                  <li>
                    <strong className="text-gray-800">Device Information:</strong>
                    <span className="text-gray-700">
                      {' '}
                      Information about your mobile device or computer, such as your IP address, device type, operating
                      system, unique device identifiers, browser type, browser version, location data, and other
                      diagnostic data.
                    </span>
                  </li>
                  <li>
                    <strong className="text-gray-800">Usage Data:</strong>
                    <span className="text-gray-700">
                      {' '}
                      Information about how you interact with the Service, such as the features you use, the time and
                      date of your visits, the pages you view, the time spent on those pages, and other diagnostic data.
                    </span>
                  </li>
                </ul>
              </div>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                2. How We Use Your Information
              </h3>

              <p className="text-justify mb-4">We use the collected information for various purposes, including to:</p>

              <ul className="ml-6 space-y-2 list-disc">
                <li>
                  <strong className="text-gray-800">Provide and Maintain the Service:</strong>
                  <span className="text-gray-700">
                    {' '}
                    To operate and deliver the core functionality of the STATDOG app.
                  </span>
                </li>
                <li>
                  <strong className="text-gray-800">Manage Your Account:</strong>
                  <span className="text-gray-700"> To register you as a user and to manage your account.</span>
                </li>
                <li>
                  <strong className="text-gray-800">Improve Our Service:</strong>
                  <span className="text-gray-700">
                    {' '}
                    To analyze usage trends and enhance the features, functionality, and user experience of STATDOG.
                  </span>
                </li>
                <li>
                  <strong className="text-gray-800">Communicate with You:</strong>
                  <span className="text-gray-700"> To send you updates, security alerts, and support messages.</span>
                </li>
                <li>
                  <strong className="text-gray-800">Monitor Service Usage:</strong>
                  <span className="text-gray-700">
                    {' '}
                    To detect, prevent, and address technical issues and to monitor the performance of the Service.
                  </span>
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                3. How We Share Your Information
              </h3>

              <p className="text-justify mb-4">We may share your information in the following situations:</p>

              <ul className="ml-6 space-y-2 list-disc">
                <li>
                  <strong className="text-gray-800">With Your Consent:</strong>
                  <span className="text-gray-700">
                    {' '}
                    We may disclose your information for any purpose with your express consent (e.g., if you choose to
                    share a report or a team's statistics with another user).
                  </span>
                </li>
                <li>
                  <strong className="text-gray-800">With Service Providers:</strong>
                  <span className="text-gray-700">
                    {' '}
                    We may share your information with third-party service providers to facilitate our Service, such as
                    data hosting, analytics, and customer support. These third parties are obligated not to disclose or
                    use the information for any other purpose.
                  </span>
                </li>
                <li>
                  <strong className="text-gray-800">For Legal Compliance:</strong>
                  <span className="text-gray-700">
                    {' '}
                    We may disclose your information if required to do so by law or in response to valid requests by
                    public authorities (e.g., a court or government agency).
                  </span>
                </li>
                <li>
                  <strong className="text-gray-800">Business Transfers:</strong>
                  <span className="text-gray-700">
                    {' '}
                    In the event of a merger, acquisition, or asset sale, your information may be transferred. We will
                    provide notice before your personal information is transferred and becomes subject to a different
                    Privacy Policy.
                  </span>
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                4. Data Security
              </h3>

              <p className="text-justify mb-4">
                The security of your data is important to us. We implement commercially reasonable administrative,
                technical, and physical safeguards to protect your personal information. However, please remember that
                no method of transmission over the Internet or method of electronic storage is 100% secure. While we
                strive to use acceptable means to protect your information, we cannot guarantee its absolute security.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                5. Data Retention
              </h3>

              <p className="text-justify mb-4">
                We will retain your personal information only for as long as is necessary for the purposes set out in
                this Privacy Policy. We will also retain and use your information to the extent necessary to comply with
                our legal obligations (e.g., if we are required to retain your data to comply with applicable laws),
                resolve disputes, respond to any questions, complaints, or claims made by you or on your behalf, and
                enforce our legal agreements and policies.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                6. Children's Privacy
              </h3>

              <p className="text-justify mb-4">
                The STATDOG app is not intended for use by anyone under the age of 13 ("<strong>Children</strong>"). We
                do not knowingly collect, sell, share, rent, or transfer personally identifiable information from/of
                Children. If you are under 13, do not provide any information on our Service. If you are a parent or
                guardian and you are aware that your Child has provided us with personal information, please contact us.
                If we become aware that we have collected personal information from Children without verification of
                parental consent, we will take steps to remove that information from our servers.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                7. Your Data Protection Rights
              </h3>

              <p className="text-justify mb-4">
                Depending on your location, you may have the following data protection rights:
              </p>

              <ul className="ml-6 space-y-2 list-disc">
                <li>
                  <strong className="text-gray-800">Right to Access:</strong>
                  <span className="text-gray-700">
                    {' '}
                    You have the right to request access to the personal information we hold about you.
                  </span>
                </li>
                <li>
                  <strong className="text-gray-800">Right to Rectification:</strong>
                  <span className="text-gray-700">
                    {' '}
                    You have the right to have any incomplete or inaccurate data we hold about you corrected.
                  </span>
                </li>
                <li>
                  <strong className="text-gray-800">Right to Erasure:</strong>
                  <span className="text-gray-700">
                    {' '}
                    You have the right to request the deletion of your personal information under certain conditions.
                  </span>
                </li>
              </ul>

              <p className="text-justify mb-4">
                To exercise these rights, please contact us using the information provided below. We may ask you to
                verify your identity before responding to such requests.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                8. Links to Other Websites
              </h3>

              <p className="text-justify mb-4">
                Our Service may contain links to other websites that are not operated by us. If you click on a
                third-party link, you will be directed to that third party's site. These third parties may use cookies
                alone or in conjunction with web beacons or other tracking technologies to collect information about you
                when you use our Service. The information they collect may be associated with your personal information
                or they may collect information, including personal information, about your online activities over time
                and across different websites and other online services. They may use this information to provide you
                with interest-based (behavioral) advertising or other targeted content. We do not control these third
                parties' tracking technologies or how they may be used. We strongly advise you to review the Privacy
                Policy of every site you visit. We have no control over and assume no responsibility for the content,
                privacy policies, or practices of any third-party sites or services. These Terms do not apply to any
                information collected by any third party, including through any application or content that may link to
                or be accessible from or on our Service.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                9. Changes to This Privacy Policy
              </h3>

              <p className="text-justify mb-4">
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new
                Privacy Policy on this page and updating the "Last Updated" date at the top. You are advised to review
                this Privacy Policy periodically for any changes.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                10. Governing Law
              </h3>

              <p className="text-justify mb-4">
                This Privacy Policy is governed and construed in accordance with the laws of the State of Florida,
                United States.
              </p>

              <h3 className="text-lg font-semibold text-green-600 border-b-2 border-green-100 pb-2 mb-4 mt-6">
                11. Contact Us
              </h3>

              <p className="text-justify mb-4">
                If you have any questions about this Privacy Policy or to exercise any of your data privacy rights,
                please contact us:
              </p>

              <ul className="ml-6 space-y-2 list-disc">
                <li className="text-gray-700">
                  By email:{' '}
                  <a href="mailto:support@statdog.live" className="underline">
                    support@statdog.live
                  </a>
                </li>
                <li className="text-gray-700">
                  By visiting our{' '}
                  <Link to="/contact-us" className="text-blue-600 hover:text-blue-700 underline font-medium">
                    Contact Us page
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </ModalContent>

      <ModalActions className="bg-gray-50 border-t border-gray-200">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            <span>Sports Data Partners, LLC</span>
          </div>
          <button
            onClick={onClose}
            className="bg-green-600 text-white px-6 py-2.5 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
          >
            I Understand
          </button>
        </div>
      </ModalActions>
    </Modal>
  );
};

export default PrivacyPolicyModal;
